import { Component, inject, input, signal } from '@angular/core';
import { DocumentItemPartial, FavoriteFieldEntity } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { deltaToPlainText } from 'src/app/shared/utils/delta.util';
import {
    CALLOUT_COLORS,
    CALLOUT_STYLES,
    CalloutColorOption,
    CalloutStyleKey,
    resolveCalloutColor,
    resolveCalloutStyle,
} from '../../../../../callout-colors';
import { CustomColorSwatchComponent } from '../../custom-color-swatch/custom-color-swatch.component';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { InlineTextEditorComponent } from '../_card-base/inline-text-editor/inline-text-editor.component';
import { RaLookPanelDirective } from '../_card-base/look-panel.directive';
import { CardTypes } from '../_card-base/enums/card-types.enum';

/**
 * Card da CAIXA DE DESTAQUE (item CALLOUT).
 *
 * O card é a própria caixa como ela imprime: fundo tonal na cor do item, título em
 * caixa alta e texto. O título (`name`) edita no lugar e salva ao sair do campo; o
 * texto (`description`) também, pelo editor inline, como a descrição das mídias. A
 * cor e o estilo moram no chip "Aparência" do rail; o tamanho do texto, na barra do topo.
 */
@Component({
    selector: 'callout-card',
    templateUrl: 'callout-card.component.html',
    styleUrl: 'callout-card.component.scss',
    imports: [
        CardBaseComponent,
        CustomColorSwatchComponent,
        RaEditTooltipDirective,
        RaIconComponent,
        RaLookPanelDirective,
        InlineTextEditorComponent,
        TranslatePipe,
    ],
})
export class CalloutCardComponent {
    public field = input<DocumentItemPartial>();
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);

    public readonly cardTypes = CardTypes;
    public readonly colorOptions = CALLOUT_COLORS;
    public readonly styleOptions = CALLOUT_STYLES;

    /** Cor livre sendo arrastada no seletor do menu: o card mostra na hora, sem salvar. */
    public colorPreview = signal<string | null>(null);

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    private _editItemsService = inject(EditItemsService);

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }

    /** Família de cor do item (ausente => azul, como no PDF), ou a cor livre em prévia. */
    public currentColor(): CalloutColorOption {
        return resolveCalloutColor(this.colorPreview() ?? this.getCurrentField()?.configs?.calloutColor);
    }

    /** Estilo da caixa do item (ausente => preenchida, como no PDF). */
    public currentStyle(): CalloutStyleKey {
        return resolveCalloutStyle(this.getCurrentField()?.configs?.calloutStyle);
    }

    /** O texto tem conteúdo? (Delta vazio do editor conta como vazio.) */
    public hasText(): boolean {
        const description = this.getCurrentField()?.description;
        if (!description) return false;
        return deltaToPlainText(description).trim().length > 0;
    }

    public isBlocked(): boolean {
        return (
            this.editService.loading() ||
            this.editDataService.currentSessionIsDisabled() ||
            this.editDataService.documentIsBlocked()
        );
    }

    /** Enter confirma o título: tira o foco, e o blur salva. */
    public onTitleKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        (event.target as HTMLInputElement | null)?.blur();
    }

    /** Salva o título ao sair do campo. Mesmo nome de antes não vai ao servidor. */
    public saveTitle(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        if (!input) return;

        const field = this.getCurrentField();
        const name = input.value.trim();
        if ((field.name ?? '') === name) {
            input.value = name;
            return;
        }

        this.editService.renameField(field._id, name);
    }

    /** Troca o estilo da caixa (otimista; o serviço desfaz se o servidor recusar). */
    public selectStyle(key: CalloutStyleKey): void {
        // O menu fica aberto: cor e estilo se ajustam juntos, vendo a caixa mudar.
        const field = this.getCurrentField();
        if (!field?._id || this.currentStyle() === key) return;

        this._editItemsService.updateItemConfig(field._id, {
            ...(field.configs ?? {}),
            calloutStyle: key,
        });
    }

    /**
     * Troca a cor: família (`blue`...) ou livre (`#rrggbb`). Otimista; o serviço desfaz se o
     * servidor recusar.
     */
    public selectColor(color: string): void {
        const field = this.getCurrentField();
        if (!field?._id) return;
        const saved = resolveCalloutColor(field.configs?.calloutColor);
        const next = resolveCalloutColor(color);
        if (saved.key === next.key && saved.accent === next.accent) return;

        this._editItemsService.updateItemConfig(field._id, {
            ...(field.configs ?? {}),
            calloutColor: color,
        });
    }

    /** O seletor livre fechou: salva o #rrggbb (otimista, como as famílias) e larga a prévia. */
    public chooseCustomColor(hex: string): void {
        this.colorPreview.set(null);
        this.selectColor(hex);
    }
}
